import {useFeatureFlag} from '@github-ui/react-core/use-feature-flag'
import safeStorage from '@github-ui/safe-storage'
import {useTrackingRef} from '@github-ui/use-tracking-ref'
import {RowsIcon} from '@primer/octicons-react'
import {Button, Popover, useConfirm} from '@primer/react'
import {type HTMLAttributes, memo, useCallback, useContext, useImperativeHandle, useRef, useState} from 'react'

import {ViewTypeParam} from '../../api/view/contracts'
import {RoadmapDateFieldsMenuContext} from '../../features/roadmap/roadmap-date-fields-menu'
import {useRoadmapDateFieldsPopover} from '../../features/roadmap/use-roadmap-date-fields-popover'
import {shortcutFromEvent, SHORTCUTS} from '../../helpers/keyboard-shortcuts'
import {useMemexRootHeight} from '../../hooks/use-memex-root-height'
import {isViewConfigDirty} from '../../hooks/use-view-state-reducer/get-view-dirty-states'
import {useViews} from '../../hooks/use-views'
import {useTemplateDialog} from '../../state-providers/template-dialog/use-template-dialog'
import {useViewOptionsMenuRef} from '../../state-providers/view-options/use-view-options-menu-ref'
import {Resources} from '../../strings'
import {useOnboardingPopover} from '../onboarding-popover'
import {DirtyViewIndicator} from '../view-options/menu-anchor'
import {FilterBar, type FilterBarProps} from './filter-bar'
import {FilterContainer} from './filter-container'
import {FilterCounterLabel} from './filter-counter-label'
import {FilterInputActions} from './filter-input-actions'
import styles from './tokenized-filter-input.module.css'

const THIRTY_DAYS_IN_MS = 1000 * 60 * 60 * 24 * 30
const DISPLAY_BUTTON_ONBOARDING_STORAGE_KEY = 'memex.display_button_onboarding_dismissed'
const displayButtonOnboardingStorage = safeStorage('localStorage', {
  throwQuotaErrorsOnSet: false,
  ttl: THIRTY_DAYS_IN_MS,
})
const NARROW_VIEWPORT_MAX_WIDTH = 575

export type TokenizedFilterInputProps = {
  /**
   * The value of the input. It will be parsed and displayed as colored
   * tokens, as well as populate the input
   */
  value: string
  /**
   * A handler for the input value change
   */
  onChangeValue: (value: string) => void
  /**
   * A handler for the input keydown event
   */
  onInputKeyDown?: (e: React.KeyboardEvent<HTMLInputElement>) => void
  /**
   * A ref for the input, optional
   */
  inputRef?: React.RefObject<HTMLInputElement | null>
  /**
   * When given, a count of results is displayed to the right of the input.
   */
  filterCount?: number | null | undefined
  /**
   * An optional handler for the reset changes button, when passed the button will be shown
   */
  onResetChanges?: (e: React.MouseEvent<HTMLButtonElement> | React.KeyboardEvent<HTMLButtonElement>) => void
  /**
   * An optional handler for the save changes button, when passed the button will be shown
   */
  onSaveChanges?: (e: React.MouseEvent<HTMLButtonElement> | React.KeyboardEvent<HTMLButtonElement>) => void
  /**
   * Optional; customize text to display on the save changes button
   */
  saveButtonText?: string
  /**
   * Whether to hide the save button; defaults to false
   */
  hideSaveButton?: boolean
  /**
   * Whether to hide the reset changes button; defaults to false
   */
  hideResetChangesButton?: boolean
  /**
   * Whether to hide the counter label; defaults to false
   */
  hideCounterLabel?: boolean
  /**
   * Whether to hide the Display button; defaults to false
   */
  hideDisplayButton?: boolean
} & Pick<HTMLAttributes<HTMLInputElement>, 'aria-describedby'> &
  Pick<FilterBarProps, 'limitedProviderKeys' | 'onValidation' | 'context'>

export const TokenizedFilterInput = memo<React.PropsWithChildren<TokenizedFilterInputProps>>(
  function TokenizedFilterInput({
    value,
    onChangeValue,
    onInputKeyDown,
    inputRef: givenInputRef,
    filterCount,
    onResetChanges,
    onSaveChanges,
    saveButtonText,
    hideSaveButton = false,
    hideCounterLabel = false,
    hideResetChangesButton = false,
    hideDisplayButton = false,
    children,
    onValidation,
    limitedProviderKeys,
    context,
  }) {
    const inputRef = useRef<HTMLInputElement>(null)
    const confirm = useConfirm()
    const {shouldShowOnboardingPopover} = useOnboardingPopover()
    const {showDateFieldsPopover} = useRoadmapDateFieldsPopover()
    const roadmapDateFieldsMenu = useContext(RoadmapDateFieldsMenuContext)
    const isDateConfigurationMenuOpen = roadmapDateFieldsMenu?.isDateConfigurationMenuOpen ?? false
    const {isTemplatesDialogOpen} = useTemplateDialog()
    useImperativeHandle<HTMLInputElement | null, HTMLInputElement | null>(givenInputRef, () => inputRef.current)
    const memexRootSize = useMemexRootHeight()
    const isNarrowViewport = (memexRootSize?.clientWidth ?? Number.POSITIVE_INFINITY) <= NARROW_VIEWPORT_MAX_WIDTH

    const [isDisplayOnboardingDismissed, setIsDisplayOnboardingDismissed] = useState(
      () => displayButtonOnboardingStorage.getItem(DISPLAY_BUTTON_ONBOARDING_STORAGE_KEY) === 'dismissed',
    )

    const onInputKeyDownRef = useTrackingRef(onInputKeyDown)
    const handleInputKeyDown: React.KeyboardEventHandler<HTMLInputElement> = useCallback(
      e => {
        switch (shortcutFromEvent(e)) {
          case SHORTCUTS.ESCAPE: {
            onInputKeyDownRef.current?.(e)
            return
          }
          case SHORTCUTS.ARROW_DOWN: {
            // TODO: Should only trigger callback when Filter suggestions are hidden
            // https://github.com/github/projects-platform/issues/3408
            // onInputKeyDownRef.current?.(e)
            return
          }
          case SHORTCUTS.TAB: {
            // When a value is present, defer to default Filter component behavior
            if (value.length > 0) return
            onInputKeyDownRef.current?.(e)
            return
          }
        }
      },
      [onInputKeyDownRef, value.length],
    )

    const handleResetChanges: React.MouseEventHandler<HTMLButtonElement> = useCallback(
      event => {
        event.stopPropagation()
        onResetChanges?.(event)
        inputRef.current?.focus()
      },
      [onResetChanges],
    )

    const displayButtonEnabled = useFeatureFlag('memex_display_button_config_menu')
    const displayButtonVisible = displayButtonEnabled && !hideDisplayButton
    const {currentView, isViewStateDirty} = useViews()

    const configDirty = currentView ? isViewConfigDirty(currentView) : false
    const isConfigDirty = displayButtonVisible ? configDirty : isViewStateDirty

    const handleSaveChanges: React.MouseEventHandler<HTMLButtonElement> = useCallback(
      async event => {
        event.stopPropagation()
        if (!onSaveChanges) {
          return
        }

        if (displayButtonVisible) {
          const shouldSave = await confirm({
            title: Resources.confirmSetDefaultChangesTitle(currentView?.name),
            content: Resources.confirmSetDefaultChangesDescription,
            confirmButtonContent: Resources.saveChanges,
            confirmButtonType: 'primary',
            width: 'large',
          })

          if (shouldSave) {
            onSaveChanges(event)
          }
        } else {
          onSaveChanges(event)
        }

        inputRef.current?.focus()
      },
      [confirm, currentView?.name, displayButtonVisible, onSaveChanges],
    )

    const hasResetAction = Boolean(onResetChanges) && !hideResetChangesButton
    const hasSaveAction = Boolean(onSaveChanges) && !hideSaveButton
    const shouldRenderFilterInputActions = hasResetAction || hasSaveAction
    const {anchorRef, toggleMenu, isMenuOpen} = useViewOptionsMenuRef()

    const handleDisplayButtonClick: React.MouseEventHandler<HTMLButtonElement> = useCallback(
      event => {
        event.stopPropagation()
        toggleMenu()
      },
      [toggleMenu],
    )

    const handleDismissDisplayOnboarding = useCallback(() => {
      displayButtonOnboardingStorage.setItem(DISPLAY_BUTTON_ONBOARDING_STORAGE_KEY, 'dismissed')
      setIsDisplayOnboardingDismissed(true)

      // Set the focus back on the Display button after dismissing the popover
      anchorRef.current?.focus()
    }, [anchorRef])

    const isRoadmapLayout = currentView?.localViewState?.layout === ViewTypeParam.Roadmap
    const hasBlockingRoadmapPopover = isRoadmapLayout && (showDateFieldsPopover || isDateConfigurationMenuOpen)

    const shouldShowDisplayOnboarding =
      displayButtonVisible &&
      !shouldShowOnboardingPopover &&
      !isDisplayOnboardingDismissed &&
      !isTemplatesDialogOpen &&
      !hasBlockingRoadmapPopover

    return (
      <FilterContainer>
        <div className={styles.Box}>
          <FilterBar
            filterValue={value}
            onChange={onChangeValue}
            onValidation={onValidation}
            onKeyDown={handleInputKeyDown}
            inputRef={inputRef}
            context={context}
            limitedProviderKeys={limitedProviderKeys}
            trailingVisual={<FilterCounterLabel hideCounterLabel={hideCounterLabel} filterCount={filterCount} />}
          />
        </div>
        {(!displayButtonEnabled || shouldRenderFilterInputActions) && (
          <>
            <FilterInputActions
              hideSaveButton={hideSaveButton}
              hideResetChangesButton={hideResetChangesButton}
              onResetChanges={onResetChanges ? handleResetChanges : undefined}
              onSaveChanges={onSaveChanges && !hideSaveButton ? handleSaveChanges : undefined}
              saveButtonText={saveButtonText}
            >
              {children}
            </FilterInputActions>
            {displayButtonVisible && <div className={styles.Divider} aria-hidden="true" role="presentation" />}
          </>
        )}
        {displayButtonVisible ? (
          <div className={styles.Container}>
            <DirtyViewIndicator isViewStateDirty={isConfigDirty}>
              <Button
                ref={anchorRef}
                leadingVisual={RowsIcon}
                onClick={handleDisplayButtonClick}
                aria-haspopup="menu"
                aria-expanded={isMenuOpen}
              >
                Display
              </Button>
            </DirtyViewIndicator>
            {shouldShowDisplayOnboarding ? (
              <Popover
                relative={false}
                role="complementary"
                open
                caret={isNarrowViewport ? 'top-left' : 'top-right'}
                className={isNarrowViewport ? styles.PopoverFlipped : styles.Popover}
              >
                <Popover.Content>
                  <h2 className={styles.OnboardingTitle}>{Resources.displayButtonOnboardingTitle}</h2>
                  <p>{Resources.displayButtonOnboardingDescription}</p>
                  <Button onClick={handleDismissDisplayOnboarding}>{Resources.confirmDialog}</Button>
                </Popover.Content>
              </Popover>
            ) : null}
          </div>
        ) : null}
      </FilterContainer>
    )
  },
)

try{ TokenizedFilterInput.displayName ||= 'TokenizedFilterInput' } catch {}
try{ TokenizedFilterInput.displayName ||= 'TokenizedFilterInput' } catch {}